import { Link } from "react-router-dom";
import Logo from "../components/Logo";

/**
 * Public SMS opt-in disclosure page — shows carriers (and compliance
 * reviewers) exactly how FreightPilot AI collects SMS consent in-app.
 * Route: /sms-opt-in
 */
export default function SmsOptInPage() {
  return (
    <div className="min-h-screen relative noise-bg" data-testid="sms-opt-in-page">
      <header className="relative z-10 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-4 sm:py-5 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/" className="text-sm text-neutral-400 hover:text-white transition-colors">Home</Link>
        </div>
      </header>

      <main className="relative z-10 max-w-2xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
        <div className="overline mb-3">SMS NOTIFICATIONS</div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">
          How SMS opt-in works
        </h1>
        <p className="text-sm text-neutral-400 mt-4 leading-relaxed">
          FreightPilot AI sends optional SMS text notifications — load alerts, weekly performance
          recaps, dispatch messages, and account notifications — only to users who explicitly opt in.
          Consent is collected inside the app exactly as shown below.
        </p>

        {/* Replica of the in-app opt-in form */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="sms-opt-in-form-replica">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-3">
            In-app opt-in form (Settings → Drivers → SMS alerts)
          </div>
          <label className="flex items-start gap-3">
            <input type="checkbox" disabled className="mt-1 rounded" />
            <span className="text-sm text-white">Enable SMS alerts</span>
          </label>
          <div className="mt-3 rounded-md bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-neutral-500">
            +1 (555) 123-4567
          </div>
          <p className="text-xs text-neutral-500 mt-4 leading-relaxed" data-testid="sms-consent-language">
            By checking this box and providing your mobile number, you agree to receive recurring
            automated SMS text notifications from FreightPilot AI (load alerts, weekly recaps,
            dispatch and account messages) at the number provided. <span className="text-neutral-300">
            Consent is not a condition of purchase. Message frequency varies. Message &amp; data rates
            may apply. Reply <b>STOP</b> to cancel at any time, or <b>HELP</b> for help.</span> See our{" "}
            <Link to="/privacy" className="text-[#FF5A1F] hover:text-[#FF7038]">Privacy Policy</Link> and{" "}
            <Link to="/terms" className="text-[#FF5A1F] hover:text-[#FF7038]">Terms of Service</Link>.
          </p>
        </div>

        <div className="mt-8 space-y-4 text-sm text-neutral-400 leading-relaxed">
          <p><b className="text-white">Opting out:</b> reply <b>STOP</b> to any message for an immediate
            opt-out and a final confirmation text, or disable SMS alerts in your account settings.
            Reply <b>HELP</b> or contact contact@freightpilotai.ai · (605) 679-7303 for assistance.</p>
          <p><b className="text-white">Your data:</b> phone numbers and opt-in consent records are never
            shared with third parties or affiliates for marketing or promotional purposes.</p>
        </div>
      </main>

      <footer className="relative z-10 border-t border-white/10 py-8 text-center text-xs text-neutral-500">
        © {new Date().getFullYear()} Freight Pilot AI LLC · <Link to="/privacy" className="hover:text-white">Privacy</Link> ·{" "}
        <Link to="/terms" className="hover:text-white">Terms</Link> · <Link to="/status" className="hover:text-white">Status</Link>
      </footer>
    </div>
  );
}
